import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, Typography, Link,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { CustomerService } from '../../services/customer.service';
import { Customer } from '../../interfaces/sales/customer.interface';
import { useToast } from '../../context/ToastContext';
import { AuthService } from '../../services/auth.service';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { DataImportService } from '../../services/integration.service';
import { MasterDataStatusFilter } from '../../interfaces/masterDataStatus.interface';
import { ActiveStatusChip, DeactivateDialog, ImportExportButtons, StatusFilterField, saveBlob } from '../common/MasterDataStatus';
import SalespersonSelect, { salespersonParams } from './scope/SalespersonSelect';

const CustomerList: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const canCreate = usePermission(PERMISSIONS.CUSTOMER_CREATE);
  const canManage = usePermission(PERMISSIONS.CUSTOMER_MANAGE);
  const [status, setStatus] = useState<MasterDataStatusFilter>('ACTIVE');
  const [toDeactivate, setToDeactivate] = useState<Customer | null>(null);
  const [rows, setRows] = useState<Customer[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [owner, setOwner] = useState<string | null>(null);

  useEffect(() => { load(); }, [page, size, status, owner]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const res = await CustomerService.listByInstitution(institutionId, page, size, status, salespersonParams(owner));
      setRows(res.content);
      setTotal(res.totalElements);
    } catch (e) {
      toast.showApiError(e, 'Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const activate = async (c: Customer) => {
    try {
      await CustomerService.activate(c.id!);
      toast.showSuccess('Customer activated');
      load();
    } catch (e) {
      toast.showApiError(e, 'Failed to activate customer');
    }
  };

  const exportCsv = async () => {
    try {
      saveBlob(await DataImportService.exportCsv('CUSTOMERS'), 'customers.csv');
    } catch (e) {
      toast.showApiError(e, 'Failed to export customers');
    }
  };

  return (
    <Box sx={{ p: 3 }} data-help-page="sales.customers.page">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5">Customers</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {canManage && (
            <ImportExportButtons kind="CUSTOMERS" onImport={() => navigate('/data-import?kind=CUSTOMERS')} onExport={exportCsv} />
          )}
          {canCreate && (
            <Button data-help="sales.customers.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/sales/customers/new')}>
              New Customer
            </Button>
          )}
        </Box>
      </Box>
      <Box data-help-section="sales.customers.filter" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <StatusFilterField value={status} onChange={(v) => { setStatus(v); setPage(0); }} />
        <SalespersonSelect data-help="sales.customers.filter.salesperson"
          withUnassigned sx={{ flex: '1 1 220px', maxWidth: 320 }} value={owner}
          onChange={id => { setOwner(id); setPage(0); }}
        />
      </Box>
      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper} data-help-section="sales.customers.table">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell data-help="sales.customers.col.name">Name</TableCell>
                <TableCell data-help="sales.customers.col.contact">Contact Person</TableCell>
                <TableCell data-help="sales.customers.col.email">Email</TableCell>
                <TableCell data-help="sales.customers.col.phone">Phone</TableCell>
                <TableCell data-help="sales.customers.col.salesperson">Salesperson</TableCell>
                <TableCell data-help="sales.customers.col.status">Status</TableCell>
                {canManage && <TableCell align="right" data-help="sales.customers.col.actions">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow
                  key={r.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/sales/customers/${r.id}`)}
                >
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.contactPerson ?? '—'}</TableCell>
                  <TableCell>{r.email ?? '—'}</TableCell>
                  <TableCell>{r.phone ?? '—'}</TableCell>
                  <TableCell>{r.salespersonName ?? 'Unassigned'}</TableCell>
                  <TableCell><ActiveStatusChip active={r.active} reason={r.deactivationReason} /></TableCell>
                  {canManage && (
                    <TableCell align="right">
                      {r.active === false ? (
                        <Link data-help="sales.customer.activate" component="button" underline="hover" onClick={(e) => { e.stopPropagation(); activate(r); }}>Activate</Link>
                      ) : (
                        <Link data-help="sales.customer.deactivate" component="button" underline="hover" onClick={(e) => { e.stopPropagation(); setToDeactivate(r); }}>Deactivate</Link>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          />
        </TableContainer>
      )}
      <DeactivateDialog
        open={toDeactivate !== null}
        noun="customer"
        name={toDeactivate?.name ?? ''}
        onDeactivate={async (reason) => {
          try {
            return await CustomerService.deactivate(toDeactivate!.id!, reason);
          } catch (e) {
            toast.showApiError(e, 'Failed to deactivate customer');
            throw e;
          }
        }}
        onClose={(changed) => { setToDeactivate(null); if (changed) load(); }}
      />
    </Box>
  );
};

export default CustomerList;
